import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import Logo from "@/components/Logo";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/locations", label: "Locations" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Logo />

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive
                    ? "text-[#D4AF37]"
                    : "text-white/80 hover:text-white"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/portal/login"
          data-testid="nav-portal-login"
          className="hidden md:inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-[#D4AF37] tracking-wide transition-colors mr-1"
        >
          <User size={14} /> Portal Login
        </Link>

        <Link
          to="/apply"
          data-testid="nav-apply-cta"
          className="hidden md:inline-flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2.5 text-sm font-medium hover:bg-[#F6ECCC] transition-colors"
        >
          Apply Now
        </Link>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="md:hidden text-white p-2"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden bg-black/95 backdrop-blur-xl border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-[#D4AF37]" : "text-white"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/portal/login"
              data-testid="mobile-nav-portal-login"
              className="border border-white/20 text-white px-5 py-3 text-center text-sm font-medium inline-flex items-center justify-center gap-1.5 hover:border-[#D4AF37]/50 hover:text-[#D4AF37]"
            >
              <User size={14} /> Portal Login
            </Link>
            <Link
              to="/apply"
              data-testid="mobile-nav-apply"
              className="bg-[#D4AF37] text-black px-5 py-3 text-center text-sm font-medium"
            >
              Apply Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
